/**
 * Module contract — everything the demo accounts need, in one place.
 *
 * Owns: deciding whether the caller is a demo account, which demo org it
 *       belongs to, and serving that org's frozen analytics payloads in place
 *       of the live ones.
 * Does not own: any live endpoint. A non-demo account never reaches this code.
 *
 * The demo is fabricated data that used to live in the production tables and be
 * dragged forward by a nightly job. That coupled it to the schema, to the
 * DynamoDB sort key and to the S3 object keys, and every one of those seams
 * broke at least once. Here the payloads are the endpoints' own responses over
 * that data, captured once, so a migration cannot move them and no job runs.
 *
 * Two things deliberately stay live, because they are immutable objects in S3
 * rather than rows: bite transcripts and bite snapshots. Their keys are built
 * from slide_end_ts, which is why that one field is never re-dated below.
 */

import { useAuthStore } from "@/store/useAuthStore";
import type { SummaryContext } from "../summary/types";

/** One payload per live endpoint the demo can stand in for. */
export type DemoPayload =
  | "sessions"
  | "slide-attempts"
  | "standards"
  | "standards-context"
  | "timeline"
  | "usage-control"
  | "assessment-attempts";

export type DemoStandardsFramework = "common_core" | "nebraska";

type DemoOrg = {
  /** Object names in the bucket are "<key>-demo-<payload>.json". */
  key: string;
  viewerUserIds: number[];
  standardsFramework: DemoStandardsFramework;
  /** Endpoints served from the capture; every other one stays live. */
  servedPayloads: ReadonlySet<DemoPayload>;
};

const ALL_PAYLOADS: ReadonlySet<DemoPayload> = new Set<DemoPayload>([
  "sessions",
  "slide-attempts",
  "standards",
  "standards-context",
  "timeline",
  "usage-control",
  "assessment-attempts",
]);

/**
 * Orgs whose accounts see a demo instead of their own analytics.
 *
 * An id here silently replaces that account's real dashboard, so it must only
 * ever hold accounts that exist for demoing.
 *
 * harker: 228 is harker@gmail.com, the org admin that owns both classrooms the
 * capture covers — 126 (harker_classroom) and 240 (Fraction - Intervention
 * Group). Derived from demo_viewer_ids.sql.
 *
 * brokenbow: 3550 brokenbow_admin and 3551 brokenbow_teacher (Broken Bow PS),
 * in Nebraska standards. Only the Standards tab is served; Sessions and Usage
 * stay live. Its sessions payload is never shown: it only anchors the
 * re-dating of the timeline and exit-ticket payloads.
 */
const DEMO_ORGS: DemoOrg[] = [
  {
    key: "harker",
    viewerUserIds: [228],
    standardsFramework: "common_core",
    servedPayloads: ALL_PAYLOADS,
  },
  {
    key: "brokenbow",
    viewerUserIds: [3550, 3551],
    standardsFramework: "nebraska",
    servedPayloads: new Set<DemoPayload>([
      "standards",
      "standards-context",
      "timeline",
      "assessment-attempts",
    ]),
  },
];

/** Demo orgs by key, for the dev overlay's demo-data tool. */
export const DEMO_ORG_KEYS: readonly string[] = DEMO_ORGS.map((org) => org.key);

/**
 * Where the dev overlay stores its demo choice: an org key, or nothing for
 * live data. Read only inside `if (__DEV_OVERLAY__)`, so a prod bundle never
 * consults it.
 */
export const DEMO_ORG_OVERRIDE_KEY = "devOverlay.demoOrg";

const readDemoOrgOverride = (): string | null => {
  try {
    return localStorage.getItem(DEMO_ORG_OVERRIDE_KEY);
  } catch {
    return null;
  }
};

/**
 * The choice this page serves, read once: the views fetch on mount, and a
 * choice changed mid-page (another tab, a set without reload) must neither mix
 * orgs in the payload cache nor pull the org from under a load in flight.
 */
let pageDemoOrgOverride: string | null | undefined;
const readPageDemoOrgOverride = (): string | null => {
  if (pageDemoOrgOverride === undefined) {
    pageDemoOrgOverride = readDemoOrgOverride();
  }
  return pageDemoOrgOverride;
};

/**
 * Where the payloads are fetched from, without a trailing slash.
 *
 * The bucket is the only source — the payloads are ~14 MB that would enter git
 * history in full on every recapture, so they are not in the repository and
 * there is no local copy to fall back to. Unset means the demo is simply off;
 * see isDemoAccount.
 *
 * The bucket has to allow public GET and answer with CORS; the objects are
 * stored gzipped with Content-Encoding set, since S3 does not compress on the
 * fly and these are ~10x smaller compressed.
 */
const FIXTURE_BASE = (import.meta.env.VITE_DEMO_ANALYTICS_BASE ?? "").replace(
  /\/+$/,
  "",
);

export const isDemoFixturesConfigured = (): boolean => Boolean(FIXTURE_BASE);

/** Escape hatch out of the fixtures; see isDemoAccount. */
const LIVE_PARAM = "live";
const DAY_MS = 86_400_000;

type Json = Record<string, any>;

/**
 * Input:  none — reads the signed-in user and the URL.
 * Output: the demo org this session should be served, or null for live data.
 *
 * Without a configured origin the whole feature is off, as if it had never been
 * built: no origin means no payloads, and answering null here sends every fork
 * down its live branch rather than leaving the call sites to fail one by one.
 *
 * Dev-overlay builds serve only the overlay's choice, whoever is signed in;
 * the rest of this applies to every other build.
 *
 * Past that, the id lists decide and only they: an account on none of them never
 * sees the fixtures, whatever the URL says. `?live=1` is the one override, and
 * it only subtracts — a listed account needs some way to reach the real
 * endpoints, which is exactly what recapturing a payload takes. Nothing is
 * remembered, so dropping the param restores the id check immediately.
 *
 * The param survives everything the dashboard does to the URL on its own: tabs
 * switch on state, and the filter writers rebuild URLSearchParams from the
 * current ones rather than from scratch.
 */
const resolveDemoOrg = (): DemoOrg | null => {
  if (!FIXTURE_BASE) return null;

  // Dev builds: the fixtures come only from the overlay's choice. The id
  // lists name prod accounts, and on dev the same ids belong to whoever
  // happens to hold them.
  if (__DEV_OVERLAY__) {
    const override = readPageDemoOrgOverride();
    return DEMO_ORGS.find((o) => o.key === override) ?? null;
  }

  const userId = useAuthStore.getState().user?.id;
  if (userId == null) return null;
  const org = DEMO_ORGS.find((o) => o.viewerUserIds.includes(userId));
  if (!org) return null;

  return new URLSearchParams(window.location.search).get(LIVE_PARAM) === "1"
    ? null
    : org;
};

/** The demo org this session is served, or null for live data. */
export const activeDemoOrgKey = (): string | null =>
  resolveDemoOrg()?.key ?? null;

/** Whether this session reads `payload` from the capture instead of the API. */
export const isDemoAccount = (payload: DemoPayload): boolean =>
  resolveDemoOrg()?.servedPayloads.has(payload) ?? false;

/** Which standards the Standards tab is drawn in; live data is Common Core. */
export const demoStandardsFramework = (): DemoStandardsFramework => {
  const org = resolveDemoOrg();
  return org?.servedPayloads.has("standards")
    ? org.standardsFramework
    : "common_core";
};

/** Keyed by full URL, so a sign-in as another demo org never gets these. */
const cache = new Map<string, Promise<any>>();

/**
 * Only ever reached behind isDemoAccount, which guarantees an org to fetch for.
 *
 * A failure drops out of the cache again: the payloads are megabytes over the
 * network, so one lost connection must not pin an empty dashboard for the rest
 * of the page's life when every caller's retry is just a tab switch away.
 */
const loadFile = <T>(name: string): Promise<T> => {
  const url = `${FIXTURE_BASE}/${resolveDemoOrg()!.key}-demo-${name}.json`;
  if (!cache.has(url)) {
    cache.set(
      url,
      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `demo payload ${name} missing (${response.status})`,
            );
          }
          return response.json();
        })
        .catch((error) => {
          cache.delete(url);
          throw error;
        }),
    );
  }
  return cache.get(url) as Promise<T>;
};

/**
 * Whole days between the newest captured session and today.
 *
 * Whole days rather than exact milliseconds, so a lesson that ran at 14:30 still
 * reads 14:30. The anchor is the end of today in local time, so the newest
 * session lands on today whatever hour it ran at.
 *
 * One value for every org: their sessions payloads share a newest session.
 */
let deltaMs: number | null = null;

const resolveDeltaMs = (sessions: Json[]): number => {
  if (deltaMs !== null) return deltaMs;
  const newestEndTs = sessions.reduce(
    (max, session) => Math.max(max, Number(session.session_end_ts) || 0),
    0,
  );
  const anchor = new Date();
  anchor.setHours(23, 59, 59, 999);
  deltaMs = newestEndTs
    ? Math.max(
        0,
        Math.floor((anchor.getTime() - newestEndTs) / DAY_MS) * DAY_MS,
      )
    : 0;
  return deltaMs;
};

/** slide_end_ts is absent from both lists on purpose — see the module contract. */
const EPOCH_MS_FIELDS = [
  "session_start_ts",
  "session_end_ts",
  "slide_start_ts",
];
const ISO_FIELDS = [
  "started_at",
  "submitted_at",
  "lesson_assigned_at",
  "created_at",
  "updated_at",
];
/**
 * Bare "YYYY-MM-DD" days, which carry no time and must not gain one.
 *
 * Separate from ISO_FIELDS because Date.parse on a bare date reads it as UTC
 * midnight; shifting that and re-serialising a full ISO string would both
 * change the value's shape and, for a viewer behind UTC, land on the previous
 * day. deltaMs is a whole number of days, so date arithmetic alone suffices.
 */
const DATE_FIELDS = ["date"];

const shiftEpochMs = (value: unknown, delta: number): unknown =>
  value === null || value === undefined ? value : Number(value) + delta;

/**
 * Hour the demo's notional nightly refresh lands at, in the viewer's own clock.
 *
 * Local rather than the job's real UTC schedule: the badge renders through
 * toLocaleDateString, so an instant picked in UTC reads as the previous day for
 * every viewer far enough west — 03:12 UTC is 20:12 the day before in Pacific,
 * and the badge would say "as of yesterday" all day. Anchoring in local time
 * makes it read today from early morning on, which is what a nightly-refreshed
 * cache means to the person looking at it.
 */
const NIGHTLY_HOUR_LOCAL = 3;

/**
 * When the cache would last have been rebuilt, had the nightly job run tonight.
 *
 * Derived per load rather than shifted along with the payload, because
 * computed_at does not describe the evidence — it describes the job. In
 * production a deploy touching the mastery package bumps model_version and
 * force-rebuilds every student (refresh.py's decide_action), and attempts land
 * daily, so the real badge reads within a day of now. A frozen capture that
 * shifted this field instead would advertise the cache as months stale, which
 * is the one thing the badge exists to rule out.
 *
 * The chart below it can still end months back — a student who has sat no exit
 * ticket since June has no newer measurement, and a fresh cache saying so is
 * exactly right.
 */
const nightlyComputedAt = (): string => {
  const instant = new Date();
  instant.setHours(NIGHTLY_HOUR_LOCAL, 12, 44, 0);
  // Before this morning's run the freshest cache is still yesterday's.
  if (instant.getTime() > Date.now()) instant.setDate(instant.getDate() - 1);
  return instant.toISOString();
};

const shiftDay = (value: unknown, delta: number): unknown => {
  if (typeof value !== "string") return value;
  const parsed = Date.parse(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed)) return value;
  return new Date(parsed + delta).toISOString().slice(0, 10);
};

const shiftIso = (value: unknown, delta: number): unknown => {
  if (typeof value !== "string") return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? value : new Date(parsed + delta).toISOString();
};

/**
 * Re-date a whole payload by walking it.
 *
 * Walking rather than listing paths: the captures follow the API shape, and a
 * timestamp added to that shape later must not silently stay in 2026.
 */
const reDate = (node: any, delta: number): any => {
  if (Array.isArray(node)) return node.map((child) => reDate(child, delta));
  if (node && typeof node === "object") {
    for (const [key, value] of Object.entries(node)) {
      if (EPOCH_MS_FIELDS.includes(key)) node[key] = shiftEpochMs(value, delta);
      else if (ISO_FIELDS.includes(key)) node[key] = shiftIso(value, delta);
      else if (DATE_FIELDS.includes(key)) node[key] = shiftDay(value, delta);
      else reDate(value, delta);
    }
  }
  return node;
};

const withDelta = async <T>(build: (delta: number) => T): Promise<T> => {
  const sessions = await loadFile<Json>("sessions");
  return build(resolveDeltaMs(sessions.sessions || []));
};

/** Stands in for GET /analytics/sessions. */
export const loadDemoSessions = async (): Promise<Json> => {
  const payload = await loadFile<Json>("sessions");
  return withDelta((delta) => reDate(structuredClone(payload), delta));
};

/** Stands in for GET /analytics/sessions/{id}/bites/{idx}/slide-attempts. */
export const loadDemoSlideAttempts = async (
  sessionId: string,
  slideIdx: number,
): Promise<Json[]> => {
  const rows = await loadFile<Json[]>("slide-attempts");
  const hit = rows.find(
    (row) => row.session_id === sessionId && row.slide_idx === slideIdx,
  );
  if (!hit) return [];
  return withDelta((delta) => reDate(structuredClone(hit.response), delta));
};

/**
 * Stands in for GET /analytics/standards.
 *
 * No cells are re-derived: the endpoint used to take a mastery_until cut that a
 * frozen payload could not answer, but the cache-backed one takes no
 * point-in-time parameter at all (analytics_lc_mastery holds current state,
 * with no per-day snapshots). Only computed_at moves, so the freshness badge
 * reads as a date near the sessions on the other tabs rather than months off.
 */
export const loadDemoStandards = async (): Promise<Json> => {
  const payload = structuredClone(await loadFile<Json>("standards"));
  // computed_at is the only dated field here — cells carry mastery, evidence
  // and lesson titles, nothing time-bearing — so this needs no delta, and in
  // particular must not pull the 5 MB sessions payload to compute one.
  payload.computed_at = nightlyComputedAt();
  return payload;
};

const ROSTER_FIELDS = ["teachers", "classrooms", "students"] as const;

/**
 * Stands in for GET /analytics/standards/context, layered over the live answer.
 *
 * The Summary roster has to name the same user_ids as the standards capture, and
 * the live endpoint only sees the org's real rows, which the demo students are
 * not. The capture overrides any top-level field except viewer: both Broken Bow
 * accounts share one file, and each must still land on its own root.
 *
 * A missing or malformed capture answers with the live context, so this can
 * ship before the object is uploaded. Fetched alongside the live request; a
 * live failure still rejects.
 */
export const loadDemoStandardsContext = async (
  live: Promise<SummaryContext>,
): Promise<SummaryContext> => {
  const capture = loadFile<Partial<SummaryContext>>("standards-context")
    .then((data) => {
      // A wrong shape would crash buildSummaryModel mid-render instead.
      if (
        typeof data !== "object" ||
        data === null ||
        ROSTER_FIELDS.some(
          (field) => field in data && !Array.isArray(data[field]),
        )
      ) {
        throw new Error("demo standards-context has the wrong shape");
      }
      return structuredClone(data);
    })
    .catch((error) => {
      console.warn("demo standards-context unavailable, using live", error);
      return null;
    });
  const [context, roster] = await Promise.all([live, capture]);
  return roster ? { ...context, ...roster, viewer: context.viewer } : context;
};

/**
 * Stands in for GET /analytics/standards/timeline.
 *
 * Keyed {user_id: {standard_code: response}} rather than held as a flat list of
 * 760 endpoint responses: the caller looks up exactly one pair, and the nesting
 * halves the payload by not repeating a user_id and a standard code per series.
 *
 * A missing pair answers with an empty series rather than throwing. The capture
 * holds one series per grid cell, so a miss means the grid and this file were
 * captured apart — the drawer renders that as "no measurements yet", which is
 * the truthful reading of "this cell has no trajectory on file".
 */
export const loadDemoStandardsTimeline = async (
  userId: number,
  standardCode: string,
): Promise<Json> => {
  const payload = await loadFile<Json>("timeline");
  const series = payload[String(userId)]?.[standardCode];
  if (!series) {
    return {
      computed_at: nightlyComputedAt(),
      current_mastery: null,
      points: [],
      lessons: [],
      is_stale: false,
    };
  }
  // points[].date does need the delta: those are real measurement days, and the
  // drawer's lesson rows open the exit-ticket modal, whose submitted_at moved.
  const shifted = await withDelta((delta) =>
    reDate(structuredClone(series), delta),
  );
  shifted.computed_at = nightlyComputedAt();
  return shifted;
};

/**
 * Stands in for GET /analytics/usage-control.
 *
 * The only endpoint of the four that takes parameters: search, sort and paging
 * all happen server-side. Mirrored here rather than left to the caller so the
 * demo path answers the same request the same way — including `total`, which
 * counts matches before the page is cut (analytics.py:1576-1587).
 */
export const loadDemoUsageControl = async (params: {
  search?: string;
  sort_by?: string;
  sort_order?: string;
  limit?: number;
  offset?: number;
}): Promise<Json> => {
  const payload = await loadFile<Json>("usage-control");
  let students: Json[] = structuredClone(payload.students || []);

  const search = params.search?.trim().toLowerCase();
  if (search) {
    students = students.filter((student) =>
      String(student.student_name || "")
        .toLowerCase()
        .includes(search),
    );
  }

  const isDescending = (params.sort_order || "asc").toLowerCase() === "desc";
  const sortBy = params.sort_by || "student_name";
  students.sort((a, b) => {
    const order =
      sortBy === "student_name"
        ? String(a.student_name).toLowerCase() <
          String(b.student_name).toLowerCase()
          ? -1
          : 1
        : Number(a[sortBy] ?? 0) - Number(b[sortBy] ?? 0);
    return isDescending ? -order : order;
  });

  const total = students.length;
  const offset = params.offset ?? 0;
  const limit = params.limit ?? 50;
  return {
    students: students.slice(offset, offset + limit),
    total,
    quota: payload.quota,
  };
};

/** Stands in for GET /exit-ticket/attempts/{id}/details. */
export const loadDemoAssessmentAttempt = async (
  attemptId: number,
): Promise<Json | null> => {
  const rows = await loadFile<Json[]>("assessment-attempts");
  const hit = rows.find((row) => row.attempt_id === attemptId);
  if (!hit) return null;
  return withDelta((delta) => reDate(structuredClone(hit.response), delta));
};
